import React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../../components/ui/alert";
import { buttonVariants } from "../../../components/ui/button";
import { cn } from "../../../lib/utils";
import { formatFileSize } from "../../../lib/fileTypes";

export type ExcelViewerErrorCause = "render" | "download";

interface ExcelViewerErrorProps {
  /** Workbook size in bytes, or null if unknown. Shown as download weight. */
  sizeBytes: number | null;
  /** The file itself, so the reader can open it in desktop Excel instead. */
  downloadUrl: string;
  fileName?: string;
  /**
   * Whether the workbook failed to render (default) or never downloaded for
   * preview.
   */
  cause?: ExcelViewerErrorCause;
}

/**
 * Shown when the in-browser preview cannot render a workbook, or cannot
 * download the file to preview at all. The second case gets its own sentence
 * because the causes listed for the first describe a workbook that arrived;
 * none of them applies to one that never did.
 *
 * Deliberately one message for every render failure. We *can* distinguish them:
 * a `RangeError: Invalid string length` means some part of the zip exceeded the
 * engine's maximum string length, and reading the zip directory would even name
 * the offending entry. But every route out of here is the same, so the copy
 * offers the possible causes as a list rather than asserting one.
 *
 * That hedging is the point. The first cut of this told people an 85 MB
 * workbook was "too large", which was wrong twice over: the limit is per zip
 * entry, not per file, and the oversized entry is not necessarily a worksheet
 * (`sharedStrings.xml` and `calcChain.xml` are stringified too). The size below
 * is download weight, not a diagnosis. See MS-3667.
 */
const ExcelViewerError: React.FC<ExcelViewerErrorProps> = ({
  sizeBytes,
  downloadUrl,
  fileName,
  cause = "render",
}) => {
  const sizeHint =
    typeof sizeBytes === "number" ? ` (${formatFileSize(sizeBytes)})` : "";

  return (
    <div className="w-full h-full flex items-center justify-center p-6">
      <Alert className="max-w-md">
        <AlertTitle>This file could not be opened</AlertTitle>
        <AlertDescription>
          <p>
            {cause === "download"
              ? "The file could not be loaded for preview. Download it to open in Excel."
              : "The workbook may be corrupt, too large, or use features the preview does not support. Download it to open in Excel."}
          </p>
          {/*
            Styled as a link rather than <Button asChild>: this project's Button
            always renders a loading slot next to {children}, so Slot's
            React.Children.only rejects it. buttonVariants gives the same shadcn
            styling on a real anchor, which is also the correct element for a
            download.
          */}
          <a
            href={downloadUrl}
            // Browsers ignore `download` cross-origin, so with a signed S3 URL
            // the file saves under its content-hash key name. Set a real value
            // anyway: it is correct if the href is ever same-origin, and an
            // omitted attribute would turn this into plain navigation.
            download={fileName || "workbook.xlsx"}
            // downloadUrl is off-origin (a signed S3 URL). No target="_blank"
            // here, but a user can still middle- or cmd-click, which would hand
            // that origin a window.opener reference without this.
            rel="noopener noreferrer"
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "mt-3",
            )}
          >
            Download file{sizeHint}
          </a>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ExcelViewerError;
